import React from "react"

import {
    StyledLink,
    Text,
} from "app/static/frontend/profiles-new/components/DuplicateManagement/style"

/**
 * maps DisplayFieldType enum value to string that would fit in a sentence
 * @param  {String} accumulator current value of string listing fields
 * @param  {Number} field DisplayFieldType enum value which maps to a field
 * @param  {Number} index number representing location of value in array
 * @param  {Number} length size of initial enum array
 * @return {String} displayable text for fields with commas and conjuctions
 */
export const mapFieldToConjunctionStringList = (accumulator, field, index, length) => {
    const fieldString = DjangIO.app.deduplication.types.DuplicatedFieldTypes.by_value(field).display_key
    switch (index) {
        // last word
        case (length - 1): {
            return accumulator.concat(fieldString)
        }
        // second to last word
        case (length - 2): {
            return length === 2
                ? accumulator.concat(fieldString.concat(" and "))
                : accumulator.concat(fieldString.concat(", and "))
        }
        default:
            return accumulator.concat(fieldString.concat(", "))
    }
}

/**
 * maps DisplayFieldType enum value to string that would fit in a sentence
 * @param  {String} accumulator current value of string listing fields
 * @param  {Number} field DisplayFieldType enum value which maps to a field
 * @param  {Number} index number representing location of value in array
 * @param  {Number} length size of initial enum array
 * @return {String} comma separated displayable text for fields
 */
const mapFieldToCommaSeparatedStringList = (accumulator, field, index, length) => {
    const fieldString = DjangIO.app.deduplication.types.DuplicatedFieldTypes.by_value(field).display_key
    switch (index) {
        // last word
        case (length - 1): {
            return accumulator.concat(fieldString)
        }
        default:
            return accumulator.concat(fieldString.concat(", "))
    }
}

/**
 * maps array of enum fields to string that would fit in sentence using mapFieldToConjunctionStringList
 * @param  {Number Array} overlappingFields array of fields that are the same between two parts of Potential Duplicate
 * @param  {Func} reduceFunction function to reduce array, should return a string
 * @return {String} string that lists the fields in displayable format
 */
export const getOverlappingFieldsText = (overlappingFields, reduceFunction) => {
    const { length } = overlappingFields
    return overlappingFields.reduce(
        (str, field, index) => reduceFunction(str, field, index, length),
        ""
    )
}

/**
 * creates a path based to page based on data type and id
 * @param  {Number} dataType represents type from QuorumDataType enum
 * @param  {Number} id datumId that path will lead to
 * @return {String} path to page representing given datum
 */
const createPath = (dataType, id) => {
    const prefix = DjangIO.app.models.QuorumDataType.by_value(dataType).profile
    return `${prefix}${id}/`
}

/**
 * creates a component to render a Link
 * if text is falsy value, then it should be replaced by Unnamed Contact or equivalent values for different data types
 * @param  {String} text what the link should say
 * @param  {String} path where the link should direct
 * @param  {Number} dataType represents the dataType of the object represented by the link from QuorumDataType enum
 * @return {React.Component} LinkComponent with path and text
 */
export const createLinkComponent = (text, path, dataType) => {
    const dataTypeSingularString = DjangIO.app.models.QuorumDataType.by_value(dataType).singular
    const displayText = text || `Unnamed ${dataTypeSingularString}`
    return (
        <StyledLink
            className="styledLink"
            to={path}
        >
            {displayText}
        </StyledLink>
    )
}

/**
 * creates sentence stating the reasons two objects may be duplicates.
 * @param  {Number Array} overlappingFields array of enum values representing overlapping fields
 * @return {String} String containing the display keys of the overlapping fields and correct syntax
 */
export const getDuplicatesSentenceText = (overlappingFields) => {
    const overlappingFieldsText = getOverlappingFieldsText(overlappingFields, mapFieldToConjunctionStringList)
    return `They have the same ${overlappingFieldsText}.`
}

/**
 * creates React.Fragment to display in DatumCell
 * @param  {React.Component} mergeFromSegueLink Link component for mergeFrom datum
 * @param  {React.Component} mergeIntoSegueLink Link component for mergeInto datum
 * @return {React.Fragment} Fragment to display the info for a potential duplicate
 */
const getDuplicateNamesText = (mergeFromSegueLink, mergeIntoSegueLink) => (
    <React.Fragment>
        { mergeFromSegueLink }
        { ` // `}
        { mergeIntoSegueLink }
    </React.Fragment>
)

/**
 * function which calls other helpers to get needed data to display potential duplicate data
 * @param  {Object} potentialDuplicate object representing a potentialDuplicate
 * @return {React.Fragment} Fragment to display the info for a potential duplicate
 */
export const createTableDisplayText = (potentialDuplicate) => {
    const mergeFromPath = createPath(potentialDuplicate.data_type, potentialDuplicate.merge_from_id)
    const mergeIntoPath = createPath(potentialDuplicate.data_type, potentialDuplicate.merge_into_id)
    const mergeFromSegueLink = createLinkComponent(potentialDuplicate.merge_from_name, mergeFromPath, potentialDuplicate.data_type)
    const mergeIntoSegueLink = createLinkComponent(potentialDuplicate.merge_into_name, mergeIntoPath, potentialDuplicate.data_type)
    return getDuplicateNamesText(mergeFromSegueLink, mergeIntoSegueLink)
}


const supporter = DjangIO.app.models.QuorumDataType.supporter.value

/**
 * creates Text to display in Supporter Profile
 * @param  {React.Component} duplicateSegueLink Link component for duplicate datum
 * @param  {String} overlappingFields overlappingFieldsText string of overlapping fields made using getOverlappingFieldsText
 * @return {Styled.div} Div to display the info for a potential duplicate of a given supporter
 */
const getSupporterProfileText = (duplicateSegueLink, overlappingFields) => (
    <Text>
        { `This might be a duplicate with ` }
        { duplicateSegueLink }
        { `. They have the same ${overlappingFields}.` }
    </Text>
)

/**
 * function which calls other helpers to get needed data to display supporter profile text
 * @param  {String} duplicateName name of duplicate datum to display
 * @param  {Number} duplicateId id of duplicate datum
 * @param  {Number Array} overlappingFields array of enum values representing overlapping fields
 * @return {Styled.div} Div to display the info for a potential duplicate of a given supporter
 */
export const createSupporterProfileDisplayText = (duplicateName, duplicateId, overlappingFields) => {
    const duplicatePath = createPath(supporter, duplicateId)
    const duplicateSegueLink = createLinkComponent(duplicateName, duplicatePath, supporter)
    const overlappingFieldsText = getOverlappingFieldsText(overlappingFields, mapFieldToConjunctionStringList)
    return getSupporterProfileText(duplicateSegueLink, overlappingFieldsText)
}

/**
 * function which calls other helpers to get needed data to display a comma separated string of fields
 * @param  {Number List} overlappingFields list of numbers representing DuplicateFieldType enum values
 * @return {String} comma separated string of displayable overlapping fields text
 */
export const createCommaSeparatedFieldsText = overlappingFields => (
    getOverlappingFieldsText(overlappingFields, mapFieldToCommaSeparatedStringList)
)
